import { resolveApiBaseUrl } from "./apiBase";

const API_URL = resolveApiBaseUrl();

const getAuthToken = (): string | null => {
  const token = localStorage.getItem('idToken') || localStorage.getItem('authToken');
  return token ? token.trim() : null;
};

export type EmailLogEntry = {
  uid: string;
  to?: string;
  toList?: string[];
  subject?: string;
  status?: string;
  service?: string;
  messageId?: string | null;
  category?: string;
  preview?: string;
  errorMessage?: string | null;
  recipientCount?: number;
  overflowFrom?: string | null;
  providerSource?: string | null;
  createdAt?: string;
};

export type EmailLogsResponse = {
  logs: EmailLogEntry[];
  count: number;
};

export type UnfinishedSubmissionRow = {
  legacy: boolean;
  uid: string;
  stage?: string | null;
  publicToken?: string | null;
  formUid?: string | null;
  email?: string | null;
  draftId?: string | null;
  clientSessionId?: string | null;
  data?: Record<string, unknown> | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  sortDate?: string | null;
  subject?: string | null;
  preview?: string | null;
  to?: string | null;
};

export type UnfinishedSubmissionsResponse = {
  items: UnfinishedSubmissionRow[];
};

export const fetchUnfinishedSubmissions = async (): Promise<UnfinishedSubmissionsResponse> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const url = API_URL ? `${API_URL}/email/unfinished-submissions` : '/email/unfinished-submissions';
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to fetch unfinished submissions');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch unfinished submissions');
  }
  return {
    items: Array.isArray(data.items) ? data.items : []
  };
};

export type EmailLogsFilters = {
  status?: string;
  service?: string;
  category?: string;
  to?: string;
  subject?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
};

export const fetchEmailLogs = async (
  filters: EmailLogsFilters = {}
): Promise<EmailLogsResponse> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }

  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    queryParams.append(key, String(value));
  });

  const queryString = queryParams.toString();
  const url = API_URL
    ? `${API_URL}/email/logs${queryString ? `?${queryString}` : ''}`
    : `/email/logs${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || 'Failed to fetch email logs');
  }

  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch email logs');
  }

  return {
    logs: Array.isArray(data.logs) ? data.logs : [],
    count: typeof data.count === 'number' ? data.count : 0
  };
};

export type EmailProviderName = 'mailjet' | 'resend';

export type EmailProviderStatus = {
  activeProvider?: EmailProviderName | string;
  providerSource?: 'runtime' | 'env' | string;
  envDefaultProvider?: string;
  runtimeProvider?: EmailProviderName | null;
  availableProviders?: EmailProviderName[];
  credentialsConfigured?: Partial<Record<EmailProviderName, boolean>>;
};

export type EmailStatusResponse = {
  success: boolean;
  serviceInfo?: { service?: string; from?: string } & EmailProviderStatus;
};

export const getEmailStatus = async (): Promise<EmailStatusResponse> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const url = API_URL ? `${API_URL}/email/status` : '/email/status';
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${authToken}` }
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || 'Failed to get email status');
  }
  const data = JSON.parse(text);
  return {
    success: Boolean(data.success),
    serviceInfo: data.serviceInfo
  };
};

export const clearAllEmailLogs = async (): Promise<{ deletedCount: number }> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const url = API_URL ? `${API_URL}/email/logs` : '/email/logs';
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to clear email logs');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to clear email logs');
  }
  return {
    deletedCount: typeof data.deletedCount === 'number' ? data.deletedCount : 0
  };
};

export type SendTestEmailResponse = {
  success: boolean;
  message?: string;
  messageId?: string;
  service?: string;
};

export const getEmailProvider = async (): Promise<EmailProviderStatus & { success: boolean }> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const url = API_URL ? `${API_URL}/email/provider` : '/email/provider';
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${authToken}` }
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || 'Failed to get email provider');
  }
  const data = JSON.parse(text);
  if (!data.success) {
    throw new Error(data.message || 'Failed to get email provider');
  }
  return data;
};

export const setEmailProvider = async (
  provider: EmailProviderName
): Promise<EmailProviderStatus & { success: boolean; message?: string }> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const url = API_URL ? `${API_URL}/email/provider` : '/email/provider';
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`
    },
    body: JSON.stringify({ provider })
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || 'Failed to update email provider');
  }
  const data = JSON.parse(text);
  if (!data.success) {
    throw new Error(data.message || 'Failed to update email provider');
  }
  return data;
};

export type EmailProviderState = {
  provider: string;
  source?: "runtime" | "env" | string;
  envDefault?: string;
  available?: string[];
};

export const fetchEmailProvider = async (): Promise<EmailProviderState> => {
  const data = await getEmailProvider();
  return {
    provider: String(data.activeProvider ?? ""),
    source: data.providerSource,
    envDefault: data.envDefaultProvider,
    available: data.availableProviders,
  };
};

export const updateEmailProvider = async (provider: EmailProviderName): Promise<EmailProviderState> => {
  const data = await setEmailProvider(provider);
  return {
    provider: String(data.activeProvider ?? provider),
    source: data.providerSource,
    envDefault: data.envDefaultProvider,
    available: data.availableProviders,
  };
};

export const sendTestEmail = async (params: {
  to: string;
  subject: string;
  html: string;
}): Promise<SendTestEmailResponse> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const url = API_URL ? `${API_URL}/email/test/send` : '/email/test/send';
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`
    },
    body: JSON.stringify(params)
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to send test email');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to send test email');
  }
  return {
    success: data.success,
    message: data.message,
    messageId: data.messageId,
    service: data.service
  };
};
